import { suggestBatchName } from 'src/services/analysisStart'

// ----------------------------------------------------------------------
// A name for the next batch of a setup, in the style of the names its past batches
// had:
//   "WES run 40", "WES run 41"              -> "WES run 42"
//   "Exome 15 Sep", "Exome 22 Sep"          -> "Exome 29 Sep" (today, same style)
//   "Run 7 · 2026-09-15", "Run 8 · 2026-09-22" -> "Run 9 · 2026-09-29"
// otherwise the panel's automatic name ("WES germline 29 Sep"). A style needs at
// least two past names that follow it. A counter is the one number that goes up from
// batch to batch, wherever it sits ("Run 5 Twist v2" -> "Run 6 Twist v2"); a number
// that is the same in all of them (a kit version, "v2") is part of the name, and one
// that goes down more often than up (a sample count) is not a counter.
// ----------------------------------------------------------------------

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const FULL_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
// Month words only as months are written ("Sep", "Sept", "September"), so a word
// that starts like one ("Marathon", "Decoy") is not read as a date.
const MONTH_RE =
  '(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?'
const MIN_NAMES = 2

const pad = (n, width) => String(n).padStart(width, '0')
const monthIndex = (word) => MONTHS.findIndex((m) => word.toLowerCase().startsWith(m.toLowerCase()))
const validDay = (year, month, day) => {
  if (year < 2000 || year > 2099 || month < 0 || month > 11 || day < 1 || day > 31) return false
  const d = new Date(year, month, day)
  return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day
}

// Each date format: a pattern, a reader (match -> { year?, month, day } | null) and a
// writer (date, match) that writes today in the same style (padding, separators,
// month spelling, year or not).
const DATE_FORMATS = [
  {
    // 2026-09-15, 2026_09_15, 2026.09.15, 2026/09/15
    id: 'ymd',
    re: /(?<![\d])(\d{4})([-_./])(\d{1,2})\2(\d{1,2})(?![\d])/,
    read: (m) => ({ year: +m[1], month: +m[3] - 1, day: +m[4] }),
    write: (d, m) =>
      `${d.getFullYear()}${m[2]}${pad(d.getMonth() + 1, m[3].length)}${m[2]}${pad(d.getDate(), m[4].length)}`,
  },
  {
    // 15.09.2026, 15/09/2026, 15-09-26 (day first, as Turkish and European labs write it)
    id: 'dmy',
    re: /(?<![\d])(\d{1,2})([-./])(\d{1,2})\2(\d{4}|\d{2})(?![\d])/,
    read: (m) => ({ year: m[4].length === 2 ? 2000 + +m[4] : +m[4], month: +m[3] - 1, day: +m[1] }),
    write: (d, m) =>
      `${pad(d.getDate(), m[1].length)}${m[2]}${pad(d.getMonth() + 1, m[3].length)}${m[2]}${
        m[4].length === 2 ? pad(d.getFullYear() % 100, 2) : d.getFullYear()
      }`,
  },
  {
    // 29 Sep, 29 Sept 2026, 29 September
    id: 'dmon',
    re: new RegExp(`(?<![\\d])(\\d{1,2}) ${MONTH_RE}(,? \\d{4})?(?![\\w])`, 'i'),
    read: (m) => ({ year: m[3] ? +m[3].replace(/\D/g, '') : null, month: monthIndex(m[2]), day: +m[1] }),
    write: (d, m) =>
      `${d.getDate()} ${monthWord(d.getMonth(), m[0])}${m[3] ? `${m[3].replace(/\d{4}/, '')}${d.getFullYear()}` : ''}`,
  },
  {
    // Sep 29, Sep 29, 2026
    id: 'mond',
    re: new RegExp(`(?<![\\w])${MONTH_RE} (\\d{1,2})(,? \\d{4})?(?![\\d])`, 'i'),
    read: (m) => ({ year: m[3] ? +m[3].replace(/\D/g, '') : null, month: monthIndex(m[1]), day: +m[2] }),
    write: (d, m) =>
      `${monthWord(d.getMonth(), m[0])} ${d.getDate()}${m[3] ? `${m[3].replace(/\d{4}/, '')}${d.getFullYear()}` : ''}`,
  },
  {
    // 20260915
    id: 'ymd8',
    re: /(?<![\d])(\d{4})(\d{2})(\d{2})(?![\d])/,
    read: (m) => ({ year: +m[1], month: +m[2] - 1, day: +m[3] }),
    write: (d) => `${d.getFullYear()}${pad(d.getMonth() + 1, 2)}${pad(d.getDate(), 2)}`,
  },
]

// The month as the name spelled it: "Sep", "Sept", "September", in its case.
function monthWord(month, sample) {
  const word = (sample.match(/[A-Za-z]+/) || [''])[0]
  let out = MONTHS[month]
  if (word.length > 4) out = FULL_MONTHS[month]
  if (word === word.toUpperCase() && word.length > 1) return out.toUpperCase()
  if (word === word.toLowerCase()) return out.toLowerCase()
  return out
}

// The first date in a name: { format, match, index }, or null.
const findDate = (name) => {
  for (let i = 0; i < DATE_FORMATS.length; i += 1) {
    const format = DATE_FORMATS[i]
    const match = name.match(format.re)
    if (match) {
      const parts = format.read(match)
      if (parts && validDay(parts.year ?? 2000, parts.month, parts.day)) return { format, match, index: match.index }
    }
  }
  return null
}

const DATE_SLOT = '\u0000date\u0000'
const NUMBER_SLOT = '\u0000n\u0000'

const byNewest = (a, b) => b.at - a.at
const loose = (template) => template.toLowerCase().replace(/\s+/g, ' ').trim()

// A name split into a template: the date (if any) becomes a slot, and every number
// outside it is noted with its place. Names of one shape differ only in their numbers
// and date. A number joined to another by a separator ("15/09", "v2.1", "9:30") is part
// of something else: a date without a year, a version, a time.
const JOINED_BEFORE = /\d[./:_-]$/
const JOINED_AFTER = /^[./:_-]\d/
const parseName = (name, at) => {
  const date = findDate(name)
  let dated = name
  if (date) dated = `${name.slice(0, date.index)}${DATE_SLOT}${name.slice(date.index + date.match[0].length)}`
  const numbers = [...dated.matchAll(/\d+/g)].map((m) => ({
    index: m.index,
    text: m[0],
    value: +m[0],
    joined: JOINED_BEFORE.test(dated.slice(0, m.index)) || JOINED_AFTER.test(dated.slice(m.index + m[0].length)),
  }))
  return { name, at, date, dated, numbers, shape: loose(dated.replace(/\d+/g, NUMBER_SLOT)) }
}

// The place of the counter in names of one shape: the one number that goes up from
// batch to batch (oldest to newest it rises at least as often as it falls, and rises
// at least once). Null when no number, or more than one, does, or when a joined
// number changes (a date this does not read, which a counter must not leave stale).
const counterIndex = (group) => {
  const oldestFirst = [...group].sort((a, b) => a.at - b.at)
  const indexes = group[0].numbers.map((_, i) => i)
  const varies = (i) => new Set(group.map((p) => p.numbers[i].value)).size > 1
  if (indexes.some((i) => group[0].numbers[i].joined && varies(i))) return null
  const counting = indexes.filter((i) => {
    if (group[0].numbers[i].joined) return false
    let rises = 0
    let falls = 0
    for (let k = 1; k < oldestFirst.length; k += 1) {
      const step = oldestFirst[k].numbers[i].value - oldestFirst[k - 1].numbers[i].value
      if (step > 0) rises += 1
      else if (step < 0) falls += 1
    }
    return rises > 0 && rises >= falls
  })
  return counting.length === 1 ? counting[0] : null
}

// A parsed name with its counter set to `value` and its date to today, in its style.
const fill = (parsed, now, counter) => {
  let out = parsed.dated
  if (counter) {
    const n = parsed.numbers[counter.index]
    out = `${out.slice(0, n.index)}${pad(counter.value, counter.width)}${out.slice(n.index + n.text.length)}`
  }
  if (parsed.date) out = out.replace(DATE_SLOT, parsed.date.format.write(new Date(now), parsed.date.match))
  return out.replace(/\s+/g, ' ').trim()
}

// Names of past batches, newest first: [{ name, at }]. Plain strings are read oldest
// first.
const cleanNames = (names) =>
  (Array.isArray(names) ? names : [])
    .map((entry, index) => (typeof entry === 'string' ? { name: entry, at: index } : entry))
    .filter((entry) => entry && typeof entry.name === 'string' && entry.name.trim())
    .map((entry) => ({ name: entry.name.trim().replace(/\s+/g, ' '), at: Number(entry.at) || 0 }))
    .sort(byNewest)

// Groups of parsed names sharing a key, largest first, then the one used last.
const groupsBy = (parsed, keyOf) => {
  const groups = new Map()
  parsed.forEach((p) => {
    const key = keyOf(p)
    if (key === null) return
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(p)
  })
  return [...groups.values()].sort((a, b) => b.length - a.length || b[0].at - a[0].at)
}

/**
 * The next batch name in the style of `pastNames` ([{ name, at }], any order; plain
 * strings oldest first), or null when they share no style.
 */
export function nextNameInStyle(pastNames, now = Date.now()) {
  const parsed = cleanNames(pastNames).map((entry) => parseName(entry.name, entry.at))
  if (parsed.length < MIN_NAMES) return null

  // A counter: names of one shape whose counter goes up.
  const counted = groupsBy(parsed, (p) => (p.numbers.length ? p.shape : null))
    .filter((group) => group.length >= MIN_NAMES)
    .map((group) => ({ group, index: counterIndex(group) }))
    .filter(({ index }) => index !== null)
  if (counted.length) {
    const { group, index } = counted[0]
    const value = Math.max(...group.map((p) => p.numbers[index].value)) + 1
    const width = Math.max(...group.map((p) => p.numbers[index].text.length))
    return fill(group[0], now, { index, value, width })
  }

  // A date: the same words around a date.
  const dated = groupsBy(parsed, (p) => (p.date ? `${p.date.format.id}|${loose(p.dated)}` : null)).filter(
    (group) => group.length >= MIN_NAMES,
  )
  if (dated.length) return fill(dated[0][0], now)
  return null
}

/** Makes a name unlike the past ones: "Exome 29 Sep (2)" when "Exome 29 Sep" was used today. */
const distinctFrom = (name, pastNames) => {
  const taken = new Set(cleanNames(pastNames).map((entry) => entry.name.toLowerCase()))
  if (!taken.has(name.toLowerCase())) return name
  for (let n = 2; n < 100; n += 1) {
    const next = `${name} (${n})`
    if (!taken.has(next.toLowerCase())) return next
  }
  return name
}

/**
 * The suggested name for a setup's next batch: the style of its past batch names
 * when they share one, else the panel's automatic name ("WES germline 29 Sep").
 * setup = { workflow, pastBatchNames: [{ name, at }] }. `taken`: other names it must
 * not repeat (other setups' batches, the other suggestions on the card), since two
 * setups of one input share the automatic name.
 */
export function suggestName(setup, now = Date.now(), taken = []) {
  const past = setup?.pastBatchNames || []
  const styled = nextNameInStyle(past, now)
  const name = styled || suggestBatchName(setup?.workflow || {}, new Date(now))
  return distinctFrom(name, [...past, ...(Array.isArray(taken) ? taken : [])])
}
